import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { MasterTransaction } from '../types';
import { DashboardDateRange, DashboardTipType } from '../contexts/DashboardFiltersContext';
import { isPos, isCash, isJar, isDispute, isAllocated, isAllocatedOrPendingPOS, filterTransactionsByLocation } from '../lib/transactionUtils';
import { getSettings } from './settingsService';
import { getStaff } from './staffService';

export interface PerformanceMetrics {
  totalStaff: number;
  averageTipsPerStaff: number;
  topPerformers: { staffId?: string; posId?: string; name: string; email: string; total: number; average: number }[];
  roleDistribution: { role: string; total: number }[];
  retentionScore: number;
  trends: { date: string; amount: number }[];
}

const getLocalDateString = (date: Date) => {
    return date.getFullYear() + '-' + 
           String(date.getMonth() + 1).padStart(2, '0') + '-' + 
           String(date.getDate()).padStart(2, '0');
};

export const getStaffPerformanceMetrics = async (
  tenantId: string, 
  dateRange: DashboardDateRange, 
  tipType: DashboardTipType,
  locationId?: string
): Promise<PerformanceMetrics> => {
  const txRef = collection(db, 'tenants', tenantId, 'master_transactions');
  
  // Get timezone from settings to match DashboardSummary
  const settings = await getSettings(tenantId);
  const timeZone = settings?.timezone || 'UTC';

  const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
  });
  const [year, month, day] = formatter.format(new Date()).split('-').map(Number);
  
  const dEnd = new Date(year, month - 1, day);
  const dStart = new Date(year, month - 1, day);

  let start = '';
  let end = '';

  if (dateRange === 'last_closed_pay_period') {
    // Try to get actual last closed pay period from payroll_exports
    const exportsRef = collection(db, 'tenants', tenantId, 'payroll_exports');
    const q = query(exportsRef, where('status', '==', 'Approved'));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const sorted = snapshot.docs.map(doc => doc.data()).sort((a, b) => b.endDate.localeCompare(a.endDate));
      start = sorted[0].startDate;
      end = sorted[0].endDate;
    } else {
      dStart.setDate(dStart.getDate() - 14);
      start = getLocalDateString(dStart);
      end = getLocalDateString(dEnd);
    }
  } else if (dateRange === 'current_pay_period') {
    // Try to get actual open pay period from payroll_periods
    const periodsRef = collection(db, 'tenants', tenantId, 'payroll_periods');
    const q = query(periodsRef, where('status', '==', 'Open'));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const sorted = snapshot.docs.map(doc => doc.data()).sort((a, b) => b.startDate.localeCompare(a.startDate));
      start = sorted[0].startDate;
      end = sorted[0].endDate;
    } else {
      dStart.setDate(dStart.getDate() - 7);
      start = getLocalDateString(dStart);
      end = getLocalDateString(dEnd);
    }
  } else {
    switch(dateRange) {
        case 'today': break;
        case 'last_30_days': dStart.setDate(dStart.getDate() - 30); break;
        case 'month_to_date': dStart.setDate(1); break;
        default: dStart.setDate(dStart.getDate() - 30);
    }
    start = getLocalDateString(dStart);
    end = getLocalDateString(dEnd);
  }

  // Fetch all transactions to ensure parent lookups always succeed
  const snapshot = await getDocs(txRef);
  const allDocs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MasterTransaction));

  // Fetch staff list for reliable posId resolution
  const staffList = await getStaff(tenantId);
  const staffIdToPosMap: Record<string, string> = {};
  const staffNameToPosMap: Record<string, string> = {};
  staffList.forEach(s => {
    if (s.id && s.posId) staffIdToPosMap[s.id] = s.posId;
    if (s.name && s.posId) staffNameToPosMap[s.name.toLowerCase()] = s.posId;
  });

  const rangeTxs = allDocs.filter(t => t.tipDate >= start && t.tipDate <= end);
  const locationFilteredTxs = filterTransactionsByLocation(rangeTxs, locationId || 'all');

  const txs = locationFilteredTxs.filter(t => {
      if (!isAllocatedOrPendingPOS(t, allDocs)) return false;
      if (tipType === 'total') return true;
      if (tipType === 'pos') return isPos(t, allDocs);
      if (tipType === 'cash') return isCash(t, allDocs);
      if (tipType === 'jar') return isJar(t, allDocs);
      if (tipType === 'dispute') return isDispute(t, allDocs);
      return true;
    }).sort((a,b) => b.tipDate.localeCompare(a.tipDate));

  const staffTotals: Record<string, { staffId: string; posId: string; name: string; total: number; count: number }> = {};
  const dailyTotals: Record<string, number> = {};

  txs.forEach(tx => {
    // Staff metrics
    const staffKey = tx.staffId || tx.email || tx.staffName || 'unknown';
    const resolvedPosId = (tx.staffId && staffIdToPosMap[tx.staffId]) ||
                          (tx.staffName && staffNameToPosMap[tx.staffName.toLowerCase()]) ||
                          tx.posId || '';

    if (!staffTotals[staffKey]) {
        staffTotals[staffKey] = { 
          staffId: tx.staffId || '', 
          posId: resolvedPosId, 
          name: tx.staffName || 'Unknown', 
          total: 0, 
          count: 0 
        };
    }
    if (!staffTotals[staffKey].posId && resolvedPosId) staffTotals[staffKey].posId = resolvedPosId;
    if (!staffTotals[staffKey].staffId && tx.staffId) staffTotals[staffKey].staffId = tx.staffId;
    
    staffTotals[staffKey].total += tx.amount || 0;
    staffTotals[staffKey].count += 1;

    // Daily trends
    const date = tx.tipDate;
    dailyTotals[date] = (dailyTotals[date] || 0) + (tx.amount || 0);
  });

  const performers = Object.entries(staffTotals)
    .map(([key, data]) => ({
      email: key.includes('@') ? key : '',
      staffId: data.staffId || (key.includes('@') ? '' : key),
      posId: data.posId,
      name: data.name,
      total: data.total,
      average: data.total / data.count
    }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 5);

  const trends = Object.entries(dailyTotals)
    .map(([date, amount]) => ({ date, amount }))
    .sort((a, b) => a.date.localeCompare(b.date));

  const retentionScore = 85; 

  return {
    totalStaff: Object.keys(staffTotals).length,
    averageTipsPerStaff: Object.keys(staffTotals).length > 0 ? Object.values(staffTotals).reduce((h, s) => h + s.total, 0) / Object.keys(staffTotals).length : 0,
    topPerformers: performers,
    roleDistribution: [], // Placeholder
    retentionScore,
    trends
  };
};
